import { Color, Vector3 } from "three";
import { nextFrame } from "../../../library-js/animation/nextFrame";
import { OnAnimationFrame } from "../../../library-js/animation/OnAnimationFrame";
import { sleep } from "../../../library-js/animation/sleep";
import { HSLColor } from "../../../library-js/colors/HSLColor";
import { YBColorSpace } from "../../../library-js/colors/YBColorSpace";
import { GradientText } from "../../../library-js/dom-actions/GradientText";
import { AttributeValue } from "../../../library-js/dom-tools/AttributeValue";
import { ElementAttribute } from "../../../library-js/dom-tools/ElementAttribute";
import { ElementType } from "../../../library-js/dom-tools/ElementType";
import { OnChange } from "../../../library-js/dom-tools/EventListeners";
import { ColorStop } from "../../../library-js/dom-ui/basics/ColorStop";
import { RadialGradient, LinearGradient } from "../../../library-js/dom-ui/basics/Gradient";
import { MathX } from "../../../library-js/math/MathX";
import { Arrays } from "../../../library-js/tools/Arrays";
import { VerticalSlider, VerticalSliderElement } from "../../../library-templates/sliders/VerticalSlider";

export class ColorGradientsValues
{
  color = new HSLColor(0,0,0,1);
  offset = new Vector3();
  clamp = false;
  smoothHue = false;
  smoothHueRadius = 30;
  flipped = false;
  blocks = 3;
}

export class ColorGradients
{
  static type = new ElementType( "color-gradients" );
  static valueSlider = new ElementType( "value-slider");
  static valueDisplay = new ElementType( "value-display" );

  static colorBlocks = new ElementType( "color-blocks" );
  static colorBlock =  new ElementType<HTMLElement>( "color-block" );
  static animateHue = new ElementAttribute( "animate-hue" );
  static interpolation = new ElementAttribute( "interpolation" );

  static linear = new AttributeValue( ElementAttribute.value, "Linear" );
  static linearFlipped = new AttributeValue( ElementAttribute.value, "Linear-Flipped" );
  static clamped = new AttributeValue( ElementAttribute.value, "Clamped" );

  static clampedBlended15 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-15" );
  static clampedBlended30 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-30" );
  static clampedBlended60 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-60" );
  static clampedBlendedFast15 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-Fast-15" );
  static clampedBlendedFast30 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-Fast-30" );
  static clampedBlendedFast60 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-Fast-60" );


  static range = new ElementAttribute( "range" );
  static value = new ElementAttribute( "value" );
  static formula = new ElementAttribute( "formula" );
  static fraction = new ElementAttribute( "fraction" );
  static sign = new ElementAttribute( "sign" );
  static animationCallbackAssigned = false;
  static animatedElements:HTMLElement[] = [];

  static applyOnDocument( onAnimationFrame:OnAnimationFrame )
  {
    if ( ! this.animationCallbackAssigned )
    {
      this.animationCallbackAssigned = true;
      onAnimationFrame.addListener( ()=>{ ColorGradients.animate() } );
    }

    this.animatedElements = [];

    ColorGradients.type.forAllInDoc( e => ColorGradients.applyOnElement( e ) );
  }

  static animate()
  {
    let durationMs = 6000;
    
    let time = new Date().getTime() % durationMs;
    let phase = time / durationMs;

    ColorGradients.animatedElements.forEach(
      ( e )=>
      {
        let hueSlider = ColorGradients.getSlider( e, new ElementAttribute( "h" ) );

        hueSlider._value.value.value = phase;
      }
    );
  }

  static async applyOnElement( e:Element )
  {
    if ( ColorGradients.animateHue.isQueriedChecked( e ) )
    {
      Arrays.pushIfNotInside( ColorGradients.animatedElements, e as HTMLElement );
    }

    let sliders = this.valueSlider.queryAll( e );
    sliders.forEach( s => ColorGradients.assignSlider( e, s as HTMLElement ) );

    OnChange.add( ColorGradients.interpolation.query<HTMLSelectElement>( e ), ()=>this.updateBlocks( e ) );
    OnChange.add( ColorGradients.animateHue.query( e ), 
    ()=>
    {
      let animated = ColorGradients.animateHue.isQueriedChecked( e );

      if ( animated )
      {
        Arrays.pushIfNotInside( ColorGradients.animatedElements, e as HTMLElement );
      }
      else
      {
        Arrays.remove( ColorGradients.animatedElements, e as HTMLElement );
      }
    } 
    );

    await sleep( 500 );
    this.updateBlocks( e ); 
  }

  static updateBlocks( e:Element )
  {
    let values = new ColorGradientsValues();
    values.color = new HSLColor( 
      this.getSliderValue( e, new ElementAttribute( "h" ) ),
      this.getSliderValue( e, new ElementAttribute( "s" ) ),
      this.getSliderValue( e, new ElementAttribute( "l" ) ),
      1
    );

    values.offset = new Vector3(
      this.getSliderValue( e, new ElementAttribute( "offset-h" ) ),
      this.getSliderValue( e, new ElementAttribute( "offset-s" ) ),
      this.getSliderValue( e, new ElementAttribute( "offset-l" ) ),
    );

    let interpolation = ColorGradients.interpolation.query<HTMLSelectElement>( e );

    values.flipped = ColorGradients.linearFlipped.isSelected( interpolation );
    values.smoothHue = ColorGradients.clampedBlended15.isSelected( interpolation ) || 
                       ColorGradients.clampedBlended30.isSelected( interpolation ) ||
                       ColorGradients.clampedBlended60.isSelected( interpolation ) ||
                       ColorGradients.clampedBlendedFast15.isSelected( interpolation ) || 
                       ColorGradients.clampedBlendedFast30.isSelected( interpolation ) ||
                       ColorGradients.clampedBlendedFast60.isSelected( interpolation );;

    let isFast =  ColorGradients.clampedBlendedFast15.isSelected( interpolation ) || 
                  ColorGradients.clampedBlendedFast30.isSelected( interpolation ) ||
                  ColorGradients.clampedBlendedFast60.isSelected( interpolation );;
    values.clamp = ColorGradients.clamped.isSelected( interpolation ) || values.smoothHue;
    
    if ( values.smoothHue )
    {
      if ( isFast )
      {
        values.smoothHueRadius = ColorGradients.clampedBlendedFast15.isSelected( interpolation ) ? 15:
        ColorGradients.clampedBlendedFast30.isSelected( interpolation ) ? 30 : 60;
      }
      else
      {
        values.smoothHueRadius = ColorGradients.clampedBlended15.isSelected( interpolation ) ? 15:
        ColorGradients.clampedBlended30.isSelected( interpolation ) ? 30 : 60;
      }

    }

   
    values.blocks = this.getSliderValue( e, new ElementAttribute( "blocks" ) );
      
    let colorBlocks = ColorGradients.colorBlocks.query( e );
    colorBlocks.innerHTML = "";

    let blocks = Math.round( values.blocks )
    let numBlocks = blocks * 2 + 1;

    let gradientColors:HSLColor[] = [];

    let alphaStart = this.getSliderValue( e, new ElementAttribute( "alpha-start" ) ) / 100;
    let alphaEnd   = this.getSliderValue( e, new ElementAttribute( "alpha-end" ) ) / 100;

    for ( let i = 0; i < numBlocks; i++ )
    {
      let block = ColorGradients.colorBlock.create();
      let state = ( i - blocks ) / blocks;
      let color = values.color.clone();
      let offset = values.offset.clone().multiplyScalar( state );

      let alphaState = 1 - ( state + 1 ) / 2;
      let alphaValue = MathX.mapClamped( alphaState, alphaStart, alphaEnd, 1, 0 );
      //console.log( "a", alphaState, ">>", alphaValue );
   
      if ( values.clamp )
      {
        if ( values.smoothHue )
        {
          if ( isFast )
          {
            color.shiftClampedHueBlendedFast( offset.x, offset.y, offset.z, values.smoothHueRadius );
          }
          else
          {
            color.shiftClampedHueBlended( offset.x, offset.y, offset.z, values.smoothHueRadius );
          }

          // console.log( "Radius", values.smoothHueRadius );
          
        }
        else 
        {
          color.shiftClamped( offset.x, offset.y, offset.z );
        }
        
      }
      else
      {
        if ( values.flipped && color.h > 60 && color.h < 240 )
        {
          offset.x = -offset.x;
        }

        color.shift( offset.x, offset.y, offset.z );
      }
      
      color.a = alphaValue;
      gradientColors.push( color );
      

      block.style.backgroundColor = color + "";

      colorBlocks.appendChild( block );

     // console.log( "Block", i, state, offset, values.offset, color )
    }

    // console.log( [].concat( gradientColors ) );
    gradientColors = gradientColors.reverse();
    
    let gradient = new RadialGradient();
    gradient.colorStops = ColorStop.createFrom( gradientColors, 0.7 );
    colorBlocks.appendChild( ElementType.break.create() );

    let flare = new ElementType<HTMLElement>( "gradient-flare" ).create();

    flare.style.background = gradient.toString();

    //console.log( "gradient", gradient.toString() );
    
    colorBlocks.appendChild( flare );

    let flare2 = new ElementType<HTMLElement>( "linear-flare" ).create();
    let gradient2 = new LinearGradient();
    gradient2.position = "-90deg";
    gradient2.colorStops = ColorStop.createFrom( gradientColors, 1 );
    flare2.style.background = gradient2.toString();

    
    colorBlocks.appendChild( flare2 );

  }

  static getSlider( e:Element, attribute:ElementAttribute )
  {
    let element = attribute.query( e );
    let slider = VerticalSlider.type.query( element ) as VerticalSliderElement;

    return slider
  }

  static getSliderValue( e:Element, attribute:ElementAttribute )
  {
    let element = attribute.query( e );
    let slider = VerticalSlider.type.query( element ) as VerticalSliderElement;
    let range  = ColorGradients.range.parseNumbers( element );

    return MathX.map( slider._value.value.value, 0, 1, range[ 0 ], range[ 1 ] ); 
  }

  static async assignSlider( parent:Element, e:HTMLElement )
  {
    let slider = VerticalSlider.type.query( e ) as VerticalSliderElement;

    let display = ColorGradients.valueDisplay.query( e );
    
    let range    = ColorGradients.range.parseNumbers( e );
    let value    = ColorGradients.value.parseNumbers( e );
    let fraction = ColorGradients.fraction.parseNumbers( e );
    let sign     = ColorGradients.sign.in( e );
    

    let formula = ColorGradients.formula.from( e, "${value}" );
 
    let format = ( realValue:number ) => 
    {
      let valueString = realValue + "";

      if ( fraction != null )
      {
        if ( fraction[ 0 ] == 0 )
        {
          valueString = Math.round( realValue ) + "";
        }
        else 
        {
          valueString = realValue.toFixed( fraction[ 0 ] );
        }
      }

      if ( sign )
      {
        if ( realValue > 0 )
        {
          valueString = "-/+" + valueString;
        }
        else
        {
          valueString = "+/-" + ( valueString.replace( "-", "" ) );
        }
        
      }

      return valueString;
    };

    

    slider._value.value.addListener(
      ( )=>
      {
        let normalizedValue = slider._value.value.value;
        let realValue = MathX.map( normalizedValue, 0, 1, range[ 0 ], range[ 1 ] );
        let ybValue = YBColorSpace.hueToYB( realValue );

        let valueString = format( realValue );
        let ybString    = format( ybValue );      
        

        display.innerHTML = formula.replace( "${value}", valueString ).replace( "${ybValue}", ybString);
      }
    );
 
   

    await nextFrame();
    let normalized = MathX.normalize( value[ 0 ], range[ 0 ], range[ 1 ] );
    slider._value.value.value = normalized; 
      

    //console.log( "Set value:", value[ 0 ], normalized, range );

    await sleep( 500 );
    slider._value.value.forceDispatch();

    await sleep( 500 );
    slider._value.value.forceDispatch();

    await sleep( 500 );
    slider._value.value.forceDispatch();


    slider._value.value.addListener(
      ()=>
      {
        this.updateBlocks( parent );
      }
    );
  }

}